import type { ReactNode } from 'react';
import { Button, EmptyState, SkeletonList } from './ui';

/**
 * 表格。
 *
 * 窄屏（< 768px）**换成卡片式列表**而不是横向滚动：管理界面的字段多，
 * 横滚会让「这一行到底属于谁」变得难以辨认。两种形态由同一份 columns 定义驱动，
 * 不存在「桌面改了、移动端忘了改」的漂移。
 */

export interface Column<T> {
  key: string;
  header: ReactNode;
  render: (row: T) => ReactNode;
  /** 窄屏卡片上是否作为标题显示（默认第一列） */
  primary?: boolean;
  /** 窄屏卡片上隐藏该字段（例如纯装饰性的操作提示） */
  hideOnMobile?: boolean;
  align?: 'left' | 'right';
  className?: string;
}

export interface TableProps<T> {
  columns: Column<T>[];
  rows: T[];
  getKey: (row: T) => string;
  loading?: boolean;
  empty?: { title: string; description?: string; action?: ReactNode };
  /** 无障碍表格标题 */
  caption?: string;
}

export function Table<T>({
  columns,
  rows,
  getKey,
  loading = false,
  empty,
  caption,
}: TableProps<T>) {
  if (loading) return <SkeletonList rows={5} />;

  if (rows.length === 0) {
    return (
      <EmptyState
        title={empty?.title ?? '暂无数据'}
        {...(empty?.description === undefined ? {} : { description: empty.description })}
        {...(empty?.action === undefined ? {} : { action: empty.action })}
      />
    );
  }

  const primaryIndex = Math.max(
    0,
    columns.findIndex((column) => column.primary === true),
  );

  return (
    <>
      {/* 宽屏：真表格 */}
      <div className="hidden overflow-hidden rounded-control md:block">
        <table className="w-full border-collapse text-left">
          {caption ? <caption className="sr-only">{caption}</caption> : null}
          <thead>
            <tr className="bg-nest-2">
              {columns.map((column) => (
                <th
                  key={column.key}
                  scope="col"
                  className={`px-3 py-2 text-xs font-medium text-fg-muted ${column.align === 'right' ? 'text-right' : ''} ${column.className ?? ''}`}
                >
                  {column.header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={getKey(row)} className="border-t border-line align-middle">
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={`px-3 py-3 text-[13px] text-fg ${column.align === 'right' ? 'text-right' : ''} ${column.className ?? ''}`}
                  >
                    {column.render(row)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* 窄屏：同一定义渲染成卡片列表 */}
      <ul className="space-y-3 md:hidden">
        {rows.map((row) => (
          <li key={getKey(row)} className="rounded-control bg-nest-2 p-3">
            <div className="mb-2 text-sm font-medium text-fg">
              {columns[primaryIndex]?.render(row)}
            </div>
            <dl className="space-y-1.5">
              {columns.map((column, index) =>
                index === primaryIndex || column.hideOnMobile ? null : (
                  <div key={column.key} className="flex items-start justify-between gap-3 text-[13px]">
                    <dt className="shrink-0 text-fg-muted">{column.header}</dt>
                    <dd className="min-w-0 text-right text-fg">{column.render(row)}</dd>
                  </div>
                ),
              )}
            </dl>
          </li>
        ))}
      </ul>
    </>
  );
}

export function Pagination({
  page,
  pageSize,
  total,
  onChange,
}: {
  page: number;
  pageSize: number;
  total: number;
  onChange: (page: number) => void;
}) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  if (total <= pageSize) return null;

  return (
    <nav
      className="mt-4 flex flex-wrap items-center justify-between gap-3 text-[13px] text-fg-muted"
      aria-label="分页"
    >
      <span className="tabular">
        共 {total} 条 · 第 {page} / {totalPages} 页
      </span>
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={() => onChange(page - 1)} disabled={page <= 1}>
          上一页
        </Button>
        <Button size="sm" onClick={() => onChange(page + 1)} disabled={page >= totalPages}>
          下一页
        </Button>
      </div>
    </nav>
  );
}

/** 宽表在窄屏的兜底提示；真正需要横滚的表用它包一层，并保证不撑破容器。 */
export function ScrollArea({ children }: { children: ReactNode }) {
  return (
    <div className="-mx-1 overflow-x-auto px-1">
      <div className="min-w-[36rem]">{children}</div>
    </div>
  );
}
